import { ref, reactive, computed, watch } from 'vue'
import type { Semestre, SemestreRequest, Session } from '~/types/administration'
import type { ApiResponse } from '~/types/api'
import { useApi } from './useApi'
import { useSession } from './useSession'
import { toast } from 'vue3-toastify'
import { sortSessionsByEtat } from '@/utils/sessionHelpers'
import { applyHistoriqueParams } from '@/utils/historiqueParams'

interface SemestreState {
    semestres: Semestre[]
    semestre: Semestre | null
    loading: boolean
    errors: Record<string, string[]>
    filters: {
        search: string
        status: 'Tous' | 'Actifs' | 'Inactifs'
        session_id: number | null
        page: number
        per_page: number
    }
    pagination: {
        total: number
        current_page: number
        per_page: number
    }
}

export function useSemestres() {
    const { apiData } = useApi()
    const { sessions: allSessions, fetchSessions: fetchAllSessions } = useSession()

    const state = reactive<SemestreState>({
        semestres: [],
        semestre: null,
        loading: false,
        errors: {},
        filters: {
            search: '',
            status: 'Tous',
            session_id: null,
            page: 1,
            per_page: 15
        },
        pagination: {
            total: 0,
            current_page: 1,
            per_page: 15
        }
    })

    const formRef = ref()
    const loadingState = ref(false)
    const includeHistorique = ref(false)
    const anneeUniversitaire = ref<string | null>(null)
    let searchTimeout: NodeJS.Timeout | null = null
    // Normalise une chaîne/date en 'YYYY-MM-DD 00:00:00'
    function normalizeDateToMidnight(input: string | Date | null | undefined): string | null {
        if (!input) return null
        let date: Date
        if (input instanceof Date) {
            date = input
        } else if (typeof input === 'string') {
            const str = input.trim()
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                date = new Date(str + 'T00:00:00')
            } else if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(str)) {
                date = new Date(str.replace(' ', 'T'))
            } else {
                date = new Date(str)
            }
        } else {
            return null
        }
        if (isNaN(date.getTime())) return null
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d} 00:00:00`
    }

    // Liste des sessions valides pour le select (sessions actives)
    const allValidSessions = ref<Session[]>([])
    async function fetchValidSessions() {
        try {
            // Utiliser l'endpoint spécialisé qui respecte les filtres par groupes du backend
            // Sans light=1 : réponse complète (session + classe + filière + parcours → établissement).
            // Requis pour les pages qui affichent la hiérarchie (ex. administration/classes.vue détail session).
            const response = await apiData<ApiResponse<Session[]>>('/sessions/valides')
            if (response && response.success) {
                // Trier les sessions selon l'état : en cours → prévu → terminé → annulé
                allValidSessions.value = sortSessionsByEtat(response.data)
            } else {
                allValidSessions.value = []
            }
        } catch (e) {
            allValidSessions.value = []
        }
    }

    // Fonction pour obtenir les numéros de semestre disponibles selon le type de formation
    function getAvailableSemestreNumbers(sessionId: number | null): number[] {
        if (!sessionId) return []

        const session = allValidSessions.value.find(s => s.id === sessionId)

        if (!session || !session.classe) return []

        // Utiliser le champ semestre_possible de la classe au lieu des switch cases hardcodés
        if (session.classe.semestre_possible) {
            // Convertir la chaîne "1,2,3" en tableau [1, 2, 3]
            return session.classe.semestre_possible
                .split(',')
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0)
                .map((s: string) => parseInt(s))
                .filter((n: number) => !isNaN(n) && n > 0)
        }

        // Fallback par défaut si le champ n'est pas défini
        return [1, 2, 3, 4, 5, 6]
    }

    // Fonction pour vérifier si un numéro de semestre est valide pour une session donnée
    function isSemestreNumberValid(sessionId: number | null, numero: number): boolean {
        if (!sessionId || !numero) return false
        const availableNumbers = getAvailableSemestreNumbers(sessionId)
        return availableNumbers.includes(numero)
    }

    // Fonction pour réinitialiser le numéro de semestre quand la session change
    function resetSemestreNumber() {
        if (state.semestre) {
            state.semestre.numero = undefined
        }
    }

    // Fonction pour formater une date lisible FR SANS l'heure
    function formatDateReadable(dateString: string): string {
        if (!dateString) return '';

        try {
            const date = new Date(dateString.replace(' ', 'T'));
            if (isNaN(date.getTime())) return dateString; // Fallback si la date est invalide

            const options: Intl.DateTimeFormatOptions = {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            };

            return date.toLocaleDateString('fr-FR', options);
        } catch (error) {
            return dateString; // Fallback en cas d'erreur
        }
    }

    // Fonction pour calculer les périodes disponibles restantes pour les semestres
    function calculateAvailablePeriods(sessionId: number | null): {
        totalPeriod: { start: string; end: string } | null;
        availablePeriods: Array<{ numero: number; start: string; end: string; isAvailable: boolean }>;
        nextAvailablePeriod: { start: string; end: string } | null;
    } {
        if (!sessionId) {
            return {
                totalPeriod: null,
                availablePeriods: [],
                nextAvailablePeriod: null
            }
        }

        const session = allValidSessions.value.find(s => s.id === sessionId)
        if (!session) {
            return {
                totalPeriod: null,
                availablePeriods: [],
                nextAvailablePeriod: null
            }
        }

        const totalStart = session.date_rentree_previsionnel
        const totalEnd = session.date_fin_previsionnel
        const totalPeriod = { start: totalStart, end: totalEnd }

        // Récupérer les semestres existants pour cette session (exclure les supprimés)
        const existingSemestres = state.semestres.filter(s => s.session_id === sessionId && !s.is_deleted)

        // Obtenir les numéros de semestre possibles pour cette classe
        const availableNumbers = getAvailableSemestreNumbers(sessionId)

        // Calculer la durée d'un semestre en mois (approximatif)
        const sessionStart = new Date(totalStart.replace(' ', 'T'))
        const sessionEnd = new Date(totalEnd.replace(' ', 'T'))
        const totalDurationMonths = (sessionEnd.getTime() - sessionStart.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
        const semestreDurationMonths = totalDurationMonths / availableNumbers.length

        const availablePeriods = availableNumbers.map(numero => {
            // Vérifier si ce semestre existe déjà
            const existingSemestre = existingSemestres.find(s => s.numero === numero)

            if (existingSemestre) {
                // Le semestre existe, calculer la période suivante disponible
                const semestreEnd = new Date(existingSemestre.date_fin_previsionnel.replace(' ', 'T'))
                const nextStart = new Date(semestreEnd.getTime() + (24 * 60 * 60 * 1000)) // +1 jour

                return {
                    numero,
                    start: existingSemestre.date_debut_previsionnel,
                    end: existingSemestre.date_fin_previsionnel,
                    isAvailable: false,
                    nextAvailableStart: nextStart.toISOString().slice(0, 16).replace('T', ' ')
                }
            } else {
                // Le semestre n'existe pas, calculer sa période en fonction des semestres existants
                let semestreStart: Date
                let semestreEnd: Date

                // Trouver le dernier semestre créé avant celui-ci
                const previousSemestres = existingSemestres
                    .filter(s => s.numero !== undefined && s.numero < numero)
                    .sort((a, b) => (b.numero || 0) - (a.numero || 0)) // Trier par ordre décroissant

                if (previousSemestres.length > 0) {
                    // Il y a des semestres précédents, commencer le jour suivant de la fin du dernier
                    const lastSemestre = previousSemestres[0]
                    const lastSemestreEnd = new Date(lastSemestre.date_fin_previsionnel.replace(' ', 'T'))
                    semestreStart = new Date(lastSemestreEnd.getTime() + (24 * 60 * 60 * 1000)) // +1 jour
                } else {
                    // Premier semestre, commencer au début de la session
                    semestreStart = sessionStart
                }

                // Trouver le prochain semestre créé après celui-ci
                const nextSemestres = existingSemestres
                    .filter(s => s.numero !== undefined && s.numero > numero)
                    .sort((a, b) => (a.numero || 0) - (b.numero || 0)) // Trier par ordre croissant

                if (nextSemestres.length > 0) {
                    // Il y a des semestres suivants, se terminer le jour avant le début du suivant
                    const nextSemestre = nextSemestres[0]
                    const nextSemestreStart = new Date(nextSemestre.date_debut_previsionnel.replace(' ', 'T'))
                    semestreEnd = new Date(nextSemestreStart.getTime() - (24 * 60 * 60 * 1000)) // -1 jour
                } else {
                    // Dernier semestre, se terminer à la fin de la session
                    semestreEnd = sessionEnd
                }

                return {
                    numero,
                    start: semestreStart.toISOString().slice(0, 16).replace('T', ' '),
                    end: semestreEnd.toISOString().slice(0, 16).replace('T', ' '),
                    isAvailable: true
                }
            }
        })

        // Trouver la prochaine période disponible
        const nextAvailablePeriod = availablePeriods.find(p => p.isAvailable)
        const nextAvailable = nextAvailablePeriod ? {
            start: nextAvailablePeriod.start,
            end: nextAvailablePeriod.end
        } : null

        return {
            totalPeriod,
            availablePeriods,
            nextAvailablePeriod: nextAvailable
        }
    }

    // Fonction pour obtenir le hint intelligent pour un numéro de semestre
    function getSemestreHint(sessionId: number | null, numero: number): string {
        if (!sessionId || !numero) return ''

        const periods = calculateAvailablePeriods(sessionId)
        const targetPeriod = periods.availablePeriods.find(p => p.numero === numero)

        if (!targetPeriod) return ''

        if (targetPeriod.isAvailable) {
            // Semestre disponible
            return `Période suggérée : ${formatDateReadable(targetPeriod.start)} au ${formatDateReadable(targetPeriod.end)}`
        } else {
            // Semestre déjà créé, montrer la période suivante disponible
            const nextAvailable = periods.availablePeriods.find(p => p.isAvailable)
            if (nextAvailable) {
                return `Semestre ${numero} déjà créé. Prochaine période disponible : ${formatDateReadable(nextAvailable.start)} au ${formatDateReadable(nextAvailable.end)}`
            } else {
                return `Semestre ${numero} déjà créé. Aucune période disponible restante.`
            }
        }
    }

    // Fonction pour obtenir le hint global de la session
    function getSessionGlobalHint(sessionId: number | null): string {
        if (!sessionId) return ''

        const session = allValidSessions.value.find(s => s.id === sessionId)
        if (!session) return ''

        const periods = calculateAvailablePeriods(sessionId)
        const availableCount = periods.availablePeriods.filter(p => p.isAvailable).length
        const totalCount = periods.availablePeriods.length

        if (availableCount === 0) {
            return `Tous les semestres de cette session ont été créés (${totalCount}/${totalCount})`
        } else if (availableCount === totalCount) {
            return `Aucun semestre créé pour cette session. Période totale : ${formatDateReadable(periods.totalPeriod?.start || '')} au ${formatDateReadable(periods.totalPeriod?.end || '')}`
        } else {
            return `Session : ${availableCount}/${totalCount} semestres restants. Période totale : ${formatDateReadable(periods.totalPeriod?.start || '')} au ${formatDateReadable(periods.totalPeriod?.end || '')}`
        }
    }

    // Fonction pour obtenir les numéros de semestre disponibles avec leurs hints
    function getAvailableSemestreNumbersWithHints(sessionId: number | null): Array<{ numero: number; hint: string; isAvailable: boolean }> {
        if (!sessionId) return []

        const periods = calculateAvailablePeriods(sessionId)
        return periods.availablePeriods.map(period => ({
            numero: period.numero,
            hint: getSemestreHint(sessionId, period.numero),
            isAvailable: period.isAvailable
        }))
    }

    const headers = [
        { title: 'Code', align: 'start' as const, key: 'code', sortable: true },
        { title: 'Numéro semestre', align: 'start' as const, key: 'numero', sortable: true },
        { title: 'Session', align: 'start' as const, key: 'session.nom', sortable: true },
        { title: 'Début prévisionnel', align: 'start' as const, key: 'date_debut_previsionnel', sortable: true },
        { title: 'Fin prévisionnelle', align: 'start' as const, key: 'date_fin_previsionnel', sortable: true },
        { title: 'Statut', align: 'start' as const, key: 'is_active', sortable: true },
        { title: 'Actions', align: 'center' as const, key: 'actions', sortable: false }
    ]

    const rules = {
        numero: [
            (v: any) => !!v || 'Le numéro est requis',
            (v: any) => v > 0 || 'Le numéro doit être supérieur à 0',
            (v: any) => {
                // Cette règle sera appliquée dynamiquement dans le composant
                return true
            }
        ],
        date_debut_previsionnel: [
            (v: string) => !!v || 'La date de début prévisionnelle est requise',
        ],
        date_fin_previsionnel: [
            (v: string) => !!v || 'La date de fin prévisionnelle est requise',
        ],
        session_id: [(v: number) => !!v || 'La session est requise']
    }

    const filteredSemestres = computed(() => {
        return state.semestres.filter(semestre => {
            const searchTerm = state.filters.search.toLowerCase().trim();

            // Log de débogage pour vérifier la normalisation
            if (searchTerm) {
            }

            const matchSearch = !searchTerm ||
                semestre.code?.toLowerCase().includes(searchTerm);
            const matchStatus = state.filters.status === 'Tous' ||
                (state.filters.status === 'Actifs' && semestre.is_active) ||
                (state.filters.status === 'Inactifs' && !semestre.is_active);
            const matchSession = !state.filters.session_id ||
                semestre.session_id === state.filters.session_id;
            return matchSearch && matchStatus && matchSession;
        });
    });

    async function fetchSemestres(options?: { includeHistorique?: boolean; anneeUniversitaire?: string | null }) {
        try {
            loadingState.value = true;
            if (options?.includeHistorique !== undefined) {
                includeHistorique.value = options.includeHistorique
            }
            if (options?.anneeUniversitaire !== undefined) {
                anneeUniversitaire.value = options.anneeUniversitaire
            }
            const params = new URLSearchParams();
            // Ne pas envoyer 'search' au backend pour éviter une recherche sensible à la casse côté serveur.
            if (state.filters.status !== 'Tous') {
                params.append('is_active', (state.filters.status === 'Actifs').toString());
            }
            if (state.filters.session_id) {
                params.append('session_id', state.filters.session_id.toString());
            }
            params.append('per_page', 'all');
            applyHistoriqueParams(params, {
                includeHistorique: includeHistorique.value,
                anneeUniversitaire: anneeUniversitaire.value,
            })
            const response = await apiData<ApiResponse<Semestre[]>>(`/semestres?${params.toString()}`);
            if (response?.success) {
                state.semestres = response.data;
                state.pagination = {
                    total: response.data.length,
                    current_page: 1,
                    per_page: state.filters.per_page
                };
            }
        } catch (error) {
            // console.error('Error in fetchSemestres:', error);
            state.semestres = [];
        } finally {
            loadingState.value = false;
        }
    }

    async function fetchSemestre(id: number) {
        try {
            state.loading = true
            const response = await apiData<ApiResponse<Semestre>>(`/semestres/${id}`)
            if (response.success) {
                state.semestre = response.data
                return response.data
            } else {
                toast.error(response.message || 'Erreur lors de la récupération du semestre')
                return null
            }
        } catch (error) {
            toast.error("Erreur lors de la récupération du semestre")
            return null
        } finally {
            state.loading = false
        }
    }

    async function createSemestre(data: SemestreRequest) {
        errorsClear();
        try {
            const payload = {
                numero: data.numero,
                date_debut_previsionnel: normalizeDateToMidnight(data.date_debut_previsionnel) || '',
                date_fin_previsionnel: normalizeDateToMidnight(data.date_fin_previsionnel) || '',
                session_id: data.session_id,
                is_active: data.is_active
            }
            const response = await apiData<ApiResponse<Semestre>>('/semestres', {
                method: 'POST',
                body: payload
            })
            if (response.success) {
                await fetchSemestres()
                return { success: true, message: response.message || 'Semestre créé avec succès' }
            }
            if (response.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response.message || "Une erreur s'est produite" }
        } catch (error: any) {
            const response = error?.response?._data
            if (response?.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response?.message || "Une erreur s'est produite" }
        }
    }

    async function updateSemestre(id: number, data: SemestreRequest) {
        errorsClear();
        try {
            const payload = {
                numero: data.numero,
                date_debut_previsionnel: normalizeDateToMidnight(data.date_debut_previsionnel) || '',
                date_fin_previsionnel: normalizeDateToMidnight(data.date_fin_previsionnel) || '',
                session_id: data.session_id,
                is_active: data.is_active
            }
            const response = await apiData<ApiResponse<Semestre>>(`/semestres/${id}`, {
                method: 'PUT',
                body: payload
            })
            if (response.success) {
                await fetchSemestres()
                return { success: true, message: response.message || 'Semestre modifié avec succès' }
            }
            if (response.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response.message || "Une erreur s'est produite" }
        } catch (error: any) {
            const response = error?.response?._data
            if (response?.errors) {
                state.errors = response.errors
            }
            return { success: false, message: response?.message || "Une erreur s'est produite" }
        }
    }

    async function deleteSemestre(id: number) {
        try {
            const response = await apiData<ApiResponse<null>>(`/semestres/${id}`, {
                method: 'DELETE'
            })
            if (response.success) {
                await fetchSemestres()
                return { success: true, message: response.message || 'Semestre supprimé avec succès' }
            }
            return {
                success: false,
                message: response.message || "Une erreur s'est produite lors de la suppression"
            }
        } catch (error: any) {
            const response = error?.response?._data
            return {
                success: false,
                message: response?.message || "Une erreur s'est produite lors de la suppression"
            }
        }
    }

    async function toggleActive(id: number) {
        try {
            const response = await apiData<ApiResponse<Semestre>>(`/semestres/${id}/toggle-active`, {
                method: 'PATCH'
            })
            if (response.success) {
                await fetchSemestres()
                return { success: true, message: response.message || 'Statut du semestre modifié avec succès' }
            }
            return { success: false, message: response.message || "Erreur lors de la modification du statut" }
        } catch (error) {
            // console.error('Error in toggleActive:', error)
            return { success: false, message: "Erreur lors de la modification du statut" }
        }
    }

    function resetForm(initialData?: Partial<SemestreRequest>) {
        if (initialData) {
            state.semestre = { ...initialData } as Semestre
        } else {
            state.semestre = {
                id: 0,
                numero: undefined,
                date_debut_previsionnel: '',
                date_debut_effectif: null,
                date_fin_previsionnel: '',
                date_fin_effectif: null,
                code: '',
                session_id: 0,
                is_active: true,
                is_deleted: false,
                created_at: '',
                updated_at: ''
            }
        }
        state.errors = {}
    }

    function errorsClear() {
        state.errors = {};
    }

    function fetchSessionsForSelect() {
        // On utilise maintenant fetchValidSessions
        fetchValidSessions();
    }

    // Watchers ciblés (pas de refetch sur page/per_page)
    watch(() => state.filters.status, () => {
        fetchSemestres();
    })
    watch(() => state.filters.session_id, () => {
        fetchSemestres();
    })
    watch(() => state.filters.search, () => {
        if (searchTimeout) clearTimeout(searchTimeout)
        searchTimeout = setTimeout(() => fetchSemestres(), 300)
    })

    return {
        semestres: filteredSemestres,
        semestre: state.semestre,
        loading: loadingState,
        errors: state.errors,
        filters: state.filters,
        pagination: state.pagination,
        formRef,
        headers,
        rules,
        allSessions,
        allValidSessions,
        fetchSemestres,
        fetchSemestre,
        createSemestre,
        updateSemestre,
        deleteSemestre,
        toggleActive,
        resetForm,
        errorsClear,
        fetchSessionsForSelect,
        fetchValidSessions,
        getAvailableSemestreNumbers,
        isSemestreNumberValid,
        resetSemestreNumber,
        calculateAvailablePeriods,
        getSemestreHint,
        getSessionGlobalHint,
        getAvailableSemestreNumbersWithHints
    }
} 